// Faza 2B (2.6): wielokrotnego użytku hook Turnstile (explicit render).
// Payment i contact używają OSOBNYCH site key/action — hook jest sparametryzowany.
import { useEffect, useRef, useState } from "react";

const SCRIPT_ID = "cf-turnstile-script";

// siteKey/action to wartości PUBLICZNE (nie sekrety). Zwraca { containerRef, token, reset }.
export function useTurnstile({ siteKey, action, enabled = true }) {
  const [token, setToken] = useState(null);
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    let pollTimer;
    if (!enabled || !siteKey) return undefined;

    // Wstrzyknij skrypt raz (jeśli brak).
    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const doRender = () => {
      if (!isMounted || !containerRef.current || !window.turnstile) return false;
      if (widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) {}
        widgetIdRef.current = null;
      }
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme: "light",
          callback: (tok) => { if (isMounted) setToken(tok); },
          "expired-callback": () => { if (isMounted) setToken(null); },
          "error-callback": () => { if (isMounted) setToken(null); },
        });
        return true;
      } catch (e) {
        // render error — token pozostaje null (fail-closed po stronie UI)
        return false;
      }
    };

    // Retry: skrypt Turnstile i kontener (portal modala) mogą pojawić się z opóźnieniem.
    // Poll co 150ms do ~9s, aż window.turnstile + containerRef gotowe i render się uda.
    let attempts = 0;
    const tryRender = () => {
      if (!isMounted) return;
      if (widgetIdRef.current !== null) return; // już wyrenderowany
      if (doRender()) return;
      attempts += 1;
      if (attempts < 60) {
        pollTimer = window.setTimeout(tryRender, 150);
      }
    };
    tryRender();

    return () => {
      isMounted = false;
      if (pollTimer) window.clearTimeout(pollTimer);
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) {}
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, action, enabled]);

  const reset = () => {
    setToken(null);
    if (widgetIdRef.current !== null && window.turnstile) {
      try { window.turnstile.reset(widgetIdRef.current); } catch (_) {}
    }
  };

  return { containerRef, token, reset };
}
